/**
 * Portal header dropdown for switching between clients when the signed-in
 * user is a contact on more than one. Hidden if there's only one match.
 *
 * Selecting a different client calls contact.switchTo (updates Supabase
 * user_metadata) → refreshes the session so the new JWT carries the new
 * contact context → reloads the page so all queries re-fetch under the
 * new auth.
 */
import { useEffect, useRef, useState } from 'react';
import { trpc } from '../../lib/trpc.js';
import { supabase } from '../../lib/supabase.js';

export function ClientSwitcher() {
  const listQ = trpc.clientContact.listMine.useQuery(undefined, {
    staleTime: 60_000,
  });
  const switchMut = trpc.clientContact.switchTo.useMutation();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const rows = listQ.data ?? [];
  // Don't render anything if the user is only on one client — the
  // dropdown would just be noise.
  if (rows.length <= 1) return null;

  const active = rows.find((r) => r.isActive);

  const handlePick = async (contactId: string) => {
    if (switching) return;
    setSwitching(contactId);
    try {
      await switchMut.mutateAsync({ contactId });
      // Force the JWT to refresh so subsequent requests carry the new
      // contact_id. Then full reload so every query refetches.
      await supabase.auth.refreshSession();
      window.location.assign('/portal');
    } catch (err) {
      setSwitching(null);
      console.error('switch failed', err);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 items-center gap-2 rounded-lg border border-scope-border bg-white px-3 text-sm text-scope-navy hover:bg-scope-cream-warm"
      >
        <span className="font-medium">{active?.clientName ?? 'Select client'}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-lg border border-scope-border bg-white shadow-scope">
          <div className="border-b border-scope-border-soft px-3 py-2 text-2xs uppercase tracking-wider text-scope-text-muted">
            Switch client
          </div>
          <ul>
            {rows.map((row) => (
              <li key={row.contactId}>
                <button
                  onClick={() => handlePick(row.contactId)}
                  disabled={row.isActive || switching !== null}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition hover:bg-scope-cream-warm disabled:bg-scope-cream-warm/50 ${
                    row.isActive ? 'text-scope-navy' : 'text-scope-text-muted'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className={`truncate ${row.isActive ? 'font-semibold text-scope-navy' : ''}`}
                    >
                      {row.clientName}
                    </div>
                    <div className="mt-0.5 text-2xs text-scope-text-faint">
                      {row.role === 'CLIENT_ADMIN' ? 'Admin' : 'Viewer'}
                    </div>
                  </div>
                  {row.isActive && (
                    <span className="rounded bg-scope-cream-warm px-1.5 py-0.5 text-2xs font-medium text-scope-navy">
                      Active
                    </span>
                  )}
                  {switching === row.contactId && (
                    <span className="text-2xs text-scope-text-faint">Switching…</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
